/**
 * Kotlin code emitter - Functional implementation
 * Code formatting and emission utilities for Kotlin
 */

export interface KotlinCodeEmitterOptions {
  /**
   * Number of spaces for indentation
   */
  indentSize?: number;
  /**
   * Whether to use public access by default
   */
  defaultPublic?: boolean;
}

export interface KotlinCodeEmitter {
  /**
   * Generates standard file header comment
   */
  generateFileHeader(): string;

  /**
   * Generates Kotlin import statements
   */
  generateKotlinImports(imports: string[]): string;

  /**
   * Generates complete Kotlin file content with header, package, and imports
   */
  generateKotlinFile(
    packageName: string,
    imports: string[],
    content: string,
    comment?: string,
  ): string;

  /**
   * Indents code block by specified number of spaces
   */
  indent(code: string, spaces?: number): string;

  /**
   * Wraps content in braces with proper indentation
   */
  wrapInBraces(content: string, inline?: boolean): string;

  /**
   * Generates KDoc comment
   */
  generateKotlinDocComment(comment: string): string;

  /**
   * Generates multi-line KDoc comment
   */
  generateKotlinMultilineDocComment(lines: string[]): string;

  /**
   * Generates list literal in Kotlin syntax
   */
  generateKotlinList(items: string[]): string;

  /**
   * Generates MARK comment for code organization
   */
  generateMarkComment(title: string): string;

  /**
   * Capitalizes first character of a string
   */
  capitalizeFirst(str: string): string;

  /**
   * Converts camelCase to PascalCase
   */
  toPascalCase(str: string): string;

  /**
   * Generates method parameter list
   */
  generateParameterList(params: Array<{ name: string; type: string }>): string;

  /**
   * Generates Kotlin function signature
   */
  generateKotlinFunctionSignature(
    name: string,
    params: Array<{ name: string; type: string }>,
    returnType: string,
    isSuspend?: boolean,
    access?: "public" | "private" | "internal",
    isOverride?: boolean,
  ): string;

  /**
   * Generates property declaration in Kotlin
   */
  generateKotlinProperty(
    name: string,
    type: string,
    isVal?: boolean,
    isPublic?: boolean,
    defaultValue?: string,
  ): string;

  /**
   * Generates data class declaration in Kotlin
   */
  generateKotlinDataClass(
    name: string,
    properties: Array<{
      name: string;
      type: string;
      comment?: string;
      serializedName?: string;
    }>,
    isPublic?: boolean,
  ): string;

  /**
   * Generates interface declaration in Kotlin
   */
  generateKotlinInterface(
    name: string,
    methods: string[],
    properties?: string[],
    superInterfaces?: string[],
    isPublic?: boolean,
  ): string;
}

/**
 * Creates a Kotlin code emitter with the given options
 */
export function createKotlinCodeEmitter(
  options: KotlinCodeEmitterOptions = {},
): KotlinCodeEmitter {
  const { indentSize = 4, defaultPublic = true } = options;

  function generateFileHeader(): string {
    return `// This file is automatically generated by brick-codegen
// Do not edit manually - regenerate using: brick-codegen`;
  }

  function generateKotlinImports(imports: string[]): string {
    if (imports.length === 0) {
      return "";
    }
    return imports.map((imp) => `import ${imp}`).join("\n");
  }

  function generateKotlinFile(
    packageName: string,
    imports: string[],
    content: string,
    comment?: string,
  ): string {
    const header = generateFileHeader();
    const importsSection = generateKotlinImports(imports);
    const commentSection = comment ? `\n/**\n * ${comment}\n */\n` : "";

    return `${header}

package ${packageName}

${importsSection}${commentSection}

${content}
`;
  }

  function indent(code: string, spaces: number = indentSize): string {
    const indentation = " ".repeat(spaces);
    return code
      .split("\n")
      .map((line) => (line.trim() ? `${indentation}${line}` : line))
      .join("\n");
  }

  function wrapInBraces(content: string, inline: boolean = false): string {
    if (inline) {
      return `{ ${content} }`;
    }
    return `{\n${indent(content)}\n}`;
  }

  function generateKotlinDocComment(comment: string): string {
    return `// ${comment}`;
  }

  function generateKotlinMultilineDocComment(lines: string[]): string {
    return `/**\n${lines.map((line) => ` * ${line}`).join("\n")}\n */`;
  }

  function generateKotlinList(items: string[]): string {
    if (items.length === 0) {
      return "emptyList()";
    }
    return `listOf(${items.join(", ")})`;
  }

  function generateMarkComment(title: string): string {
    return `// MARK: - ${title}`;
  }

  function capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function toPascalCase(str: string): string {
    return capitalizeFirst(str);
  }

  function generateParameterList(
    params: Array<{ name: string; type: string }>,
  ): string {
    if (params.length === 0) {
      return "";
    }

    return params.map((param) => `${param.name}: ${param.type}`).join(", ");
  }

  function generateKotlinFunctionSignature(
    name: string,
    params: Array<{ name: string; type: string }>,
    returnType: string,
    isSuspend: boolean = false,
    access: "public" | "private" | "internal" = defaultPublic
      ? "public"
      : "internal",
    isOverride: boolean = false,
  ): string {
    const paramList = generateParameterList(params);
    const suspendKeyword = isSuspend ? "suspend " : "";
    const overrideKeyword = isOverride ? "override " : "";
    const accessKeyword = access !== "public" ? `${access} ` : "";
    const returnPart = returnType !== "Unit" ? `: ${returnType}` : "";

    return `${accessKeyword}${overrideKeyword}${suspendKeyword}fun ${name}(${paramList})${returnPart}`;
  }

  function generateKotlinProperty(
    name: string,
    type: string,
    isVal: boolean = true,
    isPublic: boolean = defaultPublic,
    defaultValue?: string,
  ): string {
    const access = isPublic ? "" : "private ";
    const keyword = isVal ? "val" : "var";
    const value = defaultValue ? ` = ${defaultValue}` : "";
    return `${access}${keyword} ${name}: ${type}${value}`;
  }

  function generateKotlinDataClass(
    name: string,
    properties: Array<{
      name: string;
      type: string;
      comment?: string;
      serializedName?: string;
    }>,
    isPublic: boolean = defaultPublic,
  ): string {
    const access = isPublic ? "" : "private ";

    const propsWithAnnotations = properties
      .map((prop) => {
        const comment = prop.comment
          ? `${generateKotlinMultilineDocComment(prop.comment.split('\n')).split('\n').map(line => `    ${line}`).join('\n')}\n`
          : "";
        const serializedName = prop.serializedName
          ? `    @SerializedName("${prop.serializedName}")\n`
          : "";
        return `${comment}${serializedName}    val ${prop.name}: ${prop.type}`;
      })
      .join(",\n");

    return `${access}data class ${name}(
${propsWithAnnotations}
)`;
  }

  function generateKotlinInterface(
    name: string,
    methods: string[],
    properties: string[] = [],
    superInterfaces: string[] = [],
    isPublic: boolean = defaultPublic,
  ): string {
    const access = isPublic ? "" : "private ";
    const inheritance =
      superInterfaces.length > 0 ? ` : ${superInterfaces.join(", ")}` : "";
    const propsSection =
      properties.length > 0
        ? `${properties.map((p) => `    ${p}`).join("\n")}\n\n`
        : "";
    const methodsSection = methods.map((m) => `    ${m}`).join("\n\n");

    return `${access}interface ${name}${inheritance} {
${propsSection}${methodsSection}
}`;
  }

  return {
    generateFileHeader,
    generateKotlinImports,
    generateKotlinFile,
    indent,
    wrapInBraces,
    generateKotlinDocComment,
    generateKotlinMultilineDocComment,
    generateKotlinList,
    generateMarkComment,
    capitalizeFirst,
    toPascalCase,
    generateParameterList,
    generateKotlinFunctionSignature,
    generateKotlinProperty,
    generateKotlinDataClass,
    generateKotlinInterface,
  };
}
